import React, { useState, useEffect } from "react";
import LoginPage from "../Login/LoginPage";
import { useDispatch } from "react-redux";
import { useTranslation, useI18n } from "react-i18next";
import {
  Card,
  Col,
  Tabs,
  Space,
  Descriptions,
  Alert,
  Row,
  Tag,
  Table,
  Spin,
  message,
  Button,
  Tooltip,
  Typography,
  Divider,
  Modal,
  Rate,
  Input,
} from "antd";
import { Link, useParams } from "react-router-dom";
import { formatPolishCurrency, formatTCCurrency } from "../../helpers/currency";
import { formatPolishDate, formatLocalizedDate } from "../../helpers/date";
import { isTransactionInitiatedByLoggedAccount } from "../../helpers/account";
import {
  getAuthToken,
  isLogged,
  destroyAuthToken,
} from "../../helpers/account";
import { api } from "../../config/api";
import ActionBar from "./Actions/ActionBar";
import { getAccountTransactionRoleId } from "../../helpers/account";
import { useSelector } from "react-redux";
import {
  TeamOutlined,
  CreditCardOutlined,
  IssuesCloseOutlined,
  WarningOutlined,
  CopyOutlined,
  CalendarOutlined,
  MessageOutlined,
  DeleteOutlined,
  MailOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  StarOutlined,
} from "@ant-design/icons";
import ProgressBar from "./ProgressBar/ProgressBar";
import NewItemForm from "../Item/NewItemForm";
import EditTransactionForm from "./Forms/EditTransactionForm";
import ChatWindow from "./Chat/ChatWindow";
import DeleteTransactionButton from "./Forms/DeleteTransactionButton";
import AvatarDisplay from "../Dashboard/Account/AvatarDisplay";
import { Helmet } from "react-helmet-async";
const { TextArea } = Input;

const { Title, Text } = Typography;

function TransactionPage() {
  const { t, i18n } = useTranslation();
  const [transactionPartData, setTransactionPartData] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [summaryAgreementData, setSummaryAgreementData] = useState(null);
  const [summaryDeliveryOnData, setSummaryDeliveryOnData] = useState(null);
  const [itemsData, setItemsData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentParticipationEscrowFeeData, setPaymentParticipationEscrowFeeData] = useState(null);
  const [regularConflictsData, setRegularConflictsData] = useState(null);
  const [mediatorConflictsData, setMediatorConflictsData] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingModalVisible, setRatingModalVisible] = useState(false);
  const [rating, setRating] = useState(0);

  const [ratingTarget, setRatingTarget] = useState(null);
  const dispatch = useDispatch();
  const transactionDataChanged = useSelector((state) => state.transactionDataChanged);
  const { transactionUuid } = useParams();

  const handleOpenChat = () => {
    setIsChatVisible(true);
  };

  const handleCloseChat = () => {
    setIsChatVisible(false);
  };

  const handleRateUser = (targetUser) => {
    setRatingTarget(targetUser);
    setRatingModalVisible(true);
  };

  const handleSubmitRating = () => {
    if (rating === 0) {
      message.error(t('transaction.page.pleaseSelectRating'));
      return;
    }

    const apiUrl = `${api.host}/transactions/${transactionUuid}/rate`;
    const requestData = {
      rating: rating
    };

    fetch(apiUrl, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `${getAuthToken()}`,
      }),
      body: JSON.stringify(requestData),
    })
      .then((response) => response.json())
      .then((data) => {
        message.success(t('transaction.page.ratingSubmitted'));
        setRatingModalVisible(false);
        setRating(0);
        setRatingTarget(null);
        dispatch({
          type: "transactionDataUpdated",
          payload: {
            transactionDataChanged: new Date().getSeconds(),
          },
        });
      })
      .catch((error) => {
        console.error("Error submitting rating:", error);
        message.error(t('transaction.page.ratingError'));
      });
  };

  const handleDeleteItem = (uuid) => {
    setLoading(true);
    const apiUrl = `${api.host}/transactions/delete-item/${uuid}`;

    if (isLogged()) {
      fetch(apiUrl, {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `${getAuthToken()}`,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          setLoading(false);
          dispatch({
            type: "transactionDataUpdated",
            payload: {
              transactionDataChanged: new Date().getSeconds(),
            },
          });
          message.success(t('transaction.page.taskDeleted'));
        })
        .catch((error) => {
          setLoading(false);
          console.error("Error while deleting transaction item data:", error);
        });
    }
  };

  useEffect(() => {
    const apiUrl = `${api.host}/transactions/${transactionUuid}`;

    fetch(apiUrl, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `${getAuthToken()}`,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        // Set transaction participants data
        setTransactionPartData([
          {
            key: "initiator",
            label: t('transaction.participants.initiator'),
            children: (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <AvatarDisplay
                    src={data.transaction_initiator.photo}
                    account={data.transaction_initiator}
                    size={32}
                  />
                  <Text strong>
                    {data.transaction_initiator.fullname || data.transaction_initiator.email}
                  </Text>
                  {!isTransactionInitiatedByLoggedAccount(data) && (
                    <>
                      <Button
                        type="text"
                        icon={<MailOutlined />}
                        onClick={handleOpenChat}
                        style={{ color: "#667eea" }}
                      />
                      {data.finished && (
                        <Button
                          type="text"
                          icon={<StarOutlined />}
                          onClick={() => handleRateUser(data.transaction_initiator)}
                          style={{ color: "#ffa940" }}
                          title="Oceń użytkownika"
                        />
                      )}
                    </>
                  )}
                </div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  <Tag color="purple">{data.initiator_role.name}</Tag>
                  <Tag color={data.agreement_accepted_by_initiator ? "success" : "warning"}>
                    {data.agreement_accepted_by_initiator === null
                      ? t('transaction.participants.waitingAcceptance')
                      : data.agreement_accepted_by_initiator
                      ? t('transaction.participants.accepted')
                      : t('transaction.participants.rejected')}
                  </Tag>
                  {isTransactionInitiatedByLoggedAccount(data) && <Tag>{t('transaction.participants.you')}</Tag>}
                </div>
              </div>
            ),
          },
          {
            key: "counterparty",
            label: t('transaction.participants.counterparty'),
            children: (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <AvatarDisplay
                    src={data.transaction_counterparty.photo}
                    account={data.transaction_counterparty}
                    size={32}
                  />
                  <Text strong>
                    {data.transaction_counterparty.fullname || data.transaction_counterparty.email}
                  </Text>
                  {isTransactionInitiatedByLoggedAccount(data) && (
                    <>
                      <Button
                        type="text"
                        icon={<MailOutlined />}
                        onClick={handleOpenChat}
                        style={{ color: "#667eea" }}
                      />
                      {data.finished && (
                        <Button
                          type="text"
                          icon={<StarOutlined />}
                          onClick={() => handleRateUser(data.transaction_counterparty)}
                          style={{ color: "#ffa940" }}
                          title="Oceń użytkownika"
                        />
                      )}
                    </>
                  )}
                </div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  <Tag color="purple">
                    {getAccountTransactionRoleId(data, data.transaction_counterparty) === 2
                      ? t('transaction.participants.client')
                      : t('transaction.participants.contractor')}
                  </Tag>
                  <Tag color={data.agreement_accepted_by_counterparty ? "success" : "warning"}>
                    {data.agreement_accepted_by_counterparty === null
                      ? t('transaction.participants.waitingAcceptance')
                      : data.agreement_accepted_by_counterparty
                      ? t('transaction.participants.accepted')
                      : t('transaction.participants.rejected')}
                  </Tag>
                  {sessionStorage.getItem("uuid") === data.transaction_counterparty.uuid && <Tag>{t('transaction.participants.you')}</Tag>}
                </div>
              </div>
            ),
          },
        ]);

        setSummaryAgreementData([
          {
            key: "agreement",
            label: t('transaction.agreement.content'),
            children: <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>{data.agreement}</div>,
          },
        ]);

        setSummaryDeliveryOnData([
          {
            key: "delivery",
            label: t('transaction.schedule.deliveryDate'),
            children: formatLocalizedDate(data.agreement_items_delivery_at, i18n.language, 'day'),
          },
          {
            key: "inspection",
            label: t('transaction.schedule.inspectionPeriod'),
            children: `${data.inspection_period} ${t('transaction.schedule.days')}`,
          },
        ]);

        setPaymentData([
          {
            key: "amount",
            label: t('transaction.payment.taskValue'),
            children: <Text strong style={{ fontSize: "16px", color: "#667eea" }}>{formatPolishCurrency(data.escrow_amount)}</Text>,
          },
        ]);

        setPaymentParticipationEscrowFeeData([
          {
            key: "fee",
            label: t('transaction.payment.serviceFee'),
            children: (
              <div>
                <Text>{formatTCCurrency(data.escrow_fee)}</Text>
                {data.discount > 0 && (
                  <Tag color="green" style={{ marginLeft: "8px" }}>
                    {t('transaction.payment.discount')}: {data.discount * 100}%
                  </Tag>
                )}
              </div>
            ),
          },
          {
            key: "payer",
            label: t('transaction.payment.feePayer'),
            children: (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Text>{data.escrow_fee_payer.fullname || data.escrow_fee_payer.email}</Text>
                {sessionStorage.getItem("uuid") === data.escrow_fee_payer.uuid && <Tag>{t('transaction.participants.you')}</Tag>}
              </div>
            ),
          },
        ]);

        setRegularConflictsData([
          {
            key: "conflict_status",
            label: t('transaction.conflicts.status'),
            children: data.conflict ? t('transaction.conflicts.active') : t('transaction.conflicts.none'),
          },
          {
            key: "conflict_reason",
            label: t('transaction.conflicts.reason'),
            children: data.conflict_reason || "-",
          },
        ]);

        setMediatorConflictsData([
          {
            key: "mediator_conflict",
            label: t('transaction.conflicts.mediator'),
            children: data.conflict_3rd_party_conflict ? t('transaction.conflicts.yes') : t('transaction.conflicts.no'),
          },
        ]);

        setItemsData(
          data.items.map((item) => ({
            uuid: item.uuid,
            key: item.uuid,
            name: item.name,
            price: formatPolishCurrency(item.price),
          }))
        );

        setTransactionData(data);
        setLoading(false);
      })
      .catch((error) => {
        setTimeout(() => {
          message.error(t('transactions.loggedOutSecurity'));
          destroyAuthToken();
          window.location.href = "/transactions";
        }, 2000);
        setLoading(false);
      });
  }, [transactionUuid, transactionDataChanged]);

  useEffect(() => {
    setTabs([
      {
        key: "agreement",
        label: t('transaction.agreement.title'),
        icon: <CopyOutlined />,
        children: (
          <Space size="large" direction="vertical" style={{ width: "100%" }}>
            <Descriptions layout="vertical" bordered items={summaryAgreementData} />
            {transactionData && (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <EditTransactionForm transaction={transactionData} />
                <DeleteTransactionButton
                  transactionUuid={transactionData.uuid}
                  onTransactionDeleted={() => {
                    setTimeout(() => {
                      window.location.href = "/transactions";
                    }, 2000);
                  }}
                />
              </div>
            )}
          </Space>
        ),
      },
      {
        key: "conflicts",
        label: t('transaction.conflicts.title'),
        icon: <IssuesCloseOutlined />,
        children: (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Descriptions
              title={t('transaction.conflicts.standard')}
              layout="vertical"
              bordered
              items={regularConflictsData}
            />
            <Descriptions
              title={t('transaction.conflicts.mediator')}
              layout="vertical"
              bordered
              items={mediatorConflictsData}
            />
          </Space>
        ),
      },
    ]);
  }, [summaryAgreementData, regularConflictsData, mediatorConflictsData, transactionData]);

  if (sessionStorage.getItem("authToken")) {
    return (
      <>
        <Helmet>
          <title>{transactionData?.title || t('transaction.agreement.title')} - Taskizy</title>
        </Helmet>
        
        <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "20px" }}>
          <Spin spinning={loading}>
            <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
              {/* Header */}
              <div style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
                  <Link to="/transactions">
                    <Button icon={<ArrowLeftOutlined />} type="text">
                      {t('transaction.page.return')}
                    </Button>
                  </Link>
                  <Title level={2} style={{ margin: 0, color: "#2c3e50" }}>
                    {transactionData?.title}
                  </Title>
                  {transactionData?.finished && (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                      {t('transaction.page.finished')}
                    </Tag>
                  )}
                </div>
                <Text style={{ color: "#6c757d" }}>
                  {t('transaction.page.contractFrom')} {transactionData && formatLocalizedDate(transactionData.created_at, i18n.language)}
                </Text>
              </div>

              {/* Action Bar */}
              {transactionData && !transactionData.finished && (
                <div style={{ marginBottom: "24px" }}>
                  <ActionBar transaction={transactionData} />
                </div>
              )}

              {/* Finished Status Alert */}
              {transactionData?.finished && (
                <Alert
                  showIcon
                  type="success"
                  message={transactionData.finished_transaction_status?.name}
                  style={{ marginBottom: "24px", borderRadius: "8px" }}
                />
              )}

              {/* Progress Bar */}
              <Card style={{ marginBottom: "24px", borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
                <ProgressBar transaction={transactionData} />
              </Card>

              <Row gutter={[24, 24]}>
                {/* Left Column - Main Content */}
                <Col xs={24} lg={16}>
                  <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    {/* Participants */}
                    <Card 
                      title={t('transaction.participants.title')} 
                      style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                    >
                      <Descriptions layout="vertical" items={transactionPartData} />
                    </Card>

                    {/* Agreement & Conflicts Tabs */}
                    <Card style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
                      <Tabs items={tabs} />
                    </Card>

                    {/* Tasks */}
                    <Card 
                      title={
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <TeamOutlined />
                          <span>{t('transaction.tasks.title')}</span>
                        </div>
                      }
                      extra={<NewItemForm transaction={transactionData} />}
                      style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                    >
                      <Table
                        dataSource={itemsData}
                        columns={[
                          {
                            title: t('transaction.tasks.taskName'),
                            dataIndex: "name",
                            key: "name",
                          },
                          {
                            title: t('transaction.tasks.value'),
                            dataIndex: "price",
                            key: "price",
                          },
                          {
                            title: t('transactions.actions'),
                            dataIndex: "uuid",
                            key: "actions",
                            width: 80,
                            render: (uuid) => (
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                disabled={
                                  transactionData &&
                                  ((transactionData.agreement_accepted_by_initiator &&
                                    transactionData.agreement_accepted_by_counterparty) ||
                                    transactionData.finished)
                                }
                                onClick={() => handleDeleteItem(uuid)}
                                danger
                              />
                            ),
                          },
                        ]}
                        locale={{ emptyText: t('transaction.tasks.noTasks') }}
                        pagination={false}
                      />
                    </Card>
                  </Space>
                </Col>

                {/* Right Column - Details */}
                <Col xs={24} lg={8}>
                  <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    {/* Chat Button */}
                    <Button
                      type="primary"
                      icon={<MessageOutlined />}
                      onClick={handleOpenChat}
                      size="large"
                      style={{ width: "100%", borderRadius: "8px" }}
                    >
                      {t('transaction.page.openChat')}
                    </Button>

                    {/* Delivery & Inspection */}
                    <Card 
                      title={
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <CalendarOutlined />
                          <span>{t('transaction.schedule.title')}</span>
                        </div>
                      }
                      style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                    >
                      <Descriptions layout="vertical" items={summaryDeliveryOnData} />
                    </Card>

                    {/* Payment */}
                    <Card 
                      title={
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <CreditCardOutlined />
                          <span>{t('transaction.payment.title')}</span>
                        </div>
                      }
                      style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                    >
                      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                        <Descriptions layout="vertical" items={paymentData} />
                        <Descriptions layout="vertical" items={paymentParticipationEscrowFeeData} />
                      </Space>
                    </Card>
                  </Space>
                </Col>
              </Row>
            </div>
          </Spin>

          {/* Chat Modal */}
          <Modal
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <MessageOutlined style={{ color: "#667eea" }} />
                <span>{transactionData?.title}</span>
              </div>
            }
            open={isChatVisible}
            onCancel={handleCloseChat}
            footer={null}
            width={600}
            style={{ top: 20 }}
          >
            {transactionData && <ChatWindow transactionUuid={transactionData.uuid} />}
          </Modal>
          
          {/* Rating Modal */}
          <Modal
            title={t('transaction.page.rateUser')}
            open={ratingModalVisible}
            onOk={handleSubmitRating}
            onCancel={() => {
              setRatingModalVisible(false);
              setRating(0);
              setRatingTarget(null);
            }}
            okText={t('transaction.page.sendRating')}
            cancelText={t('common.cancel')}
          >
            {ratingTarget && (
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <AvatarDisplay
                  src={ratingTarget.photo}
                  account={ratingTarget}
                  size={64}
                />
                <Title level={4} style={{ marginTop: "12px", marginBottom: "8px" }}>
                  {ratingTarget.fullname || ratingTarget.email}
                </Title>
                <Text type="secondary">{t('transaction.page.howDoYouRate')}</Text>
              </div>
            )}
            
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <Rate
                value={rating}
                onChange={setRating}
                style={{ fontSize: "32px" }}
              />
            </div>

          </Modal>
        </div>
      </>
    );
  } else {
    return <LoginPage />;
  }
}

export default TransactionPage;